import { Component, OnInit } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AuthCallbackComponent } from './core/components/auth-callback/auth-callback.component';
import { AuthRenewComponent } from './core/components/auth-renew/auth-renew.component';
import { ErrorComponent } from './core/services/errors/error/error.component';
import { NotFoundComponent } from './core/services/errors/not-found/not-found.component';
import { AuthGuardService } from './core/services/guard-services/auth-guard.service';
import { CustomRedirectGuard } from './core/services/guard-services/custom-redirect-guard.service';
import { DemoGuardService } from './core/services/guard-services/demo-guard.service';
import { OnboardingGuardService } from './core/services/guard-services/onboarding-guard.service';
import { PropertyOwnershipGuardService } from './core/services/guard-services/property-ownership-guard.service';
import { AboutUsComponent } from './features/about-us/about-us.component';
import { ContactComponent } from './features/contact/contact.component';
import { CookieSettingsComponent } from './features/cookie-settings/cookie-settings.component';
import { FaqComponent } from './features/faq/faq.component';
import { FeedbackComponent } from './features/feedback/feedback.component';
import { FinancingComponent } from './features/financing/financing.component';
import { FindPropertyComponent } from './features/find-property/find-property.component';
import { LoanApplicationWizardComponent } from './features/loan-application-wizard/loan-application-wizard.component';
import { LoanEligibilityWizardComponent } from './features/loan-eligibility-wizard/loan-eligibility-wizard.component';
import { LoginComponent } from './features/login/login.component';
import { OnboardingBuyerComponent } from './features/onboarding/onboarding-buyer/onboarding-buyer.component';
import { OnboardingSellerComponent } from './features/onboarding/onboarding-seller/onboarding-seller.component';
import { OnboardingWelcomeComponent } from './features/onboarding/onboarding-welcome/onboarding-welcome.component';
import { PaymentWizardComponent } from './features/payment-wizard/payment-wizard.component';
import { PrivacyPolicyComponent } from './features/privacy-policy/privacy-policy.component';
import { PropertyDetailsComponent } from './features/property-details/property-details.component';
import { FinalStepComponent } from './features/property-listing/complete/complete.component';
import { PropertyListingComponent } from './features/property-listing/property-listing.component';
import { PropertyUploadWizardComponent } from './features/property-upload-wizard/property-upload-wizard.component';
import { RegistrationComponent } from './features/registration/registration.component';
import { SellWithUsComponent } from './features/sell-with-us/sell-with-us.component';
import { ServiceDetailsComponent } from './features/services/service-details/service-details.component';
import { ServicePropertySelectorComponent } from './features/services/service-property-selector/service-property-selector.component';
import { ServicesComponent } from './features/services/services.component';
import { TermsAndConditionsComponent } from './features/terms-and-conditions/terms-and-conditions.component';
import { PortalShellComponent } from './layout/components/portal-shell/portal-shell.component';
import { CheckoutFailComponent } from './shared/components/checkout/checkout-fail/checkout-fail.component';
import { CheckoutProcessingComponent } from './shared/components/checkout/checkout-processing/checkout-processing.component';
import { CheckoutResultComponent } from './shared/components/checkout/checkout-result/checkout-result.component';
import { CheckoutComponent } from './shared/components/checkout/checkout.component';

@Component({
  template: '', // Empty template since this is just for redirection
})
export class RedirectComponent implements OnInit {
  constructor(private router: Router) {}

  ngOnInit() {
    // Get language from localStorage (fallback to 'en' if not found)
    const storedLang = localStorage.getItem('translocoLang') || 'el';
    // Programmatically navigate to the language-specific home route
    // Prevent redirect loops
    if (storedLang && storedLang !== 'undefined') {
      this.router.navigateByUrl(`/${storedLang}/home`);
    } else {
      this.router.navigateByUrl('/el/home');
    }
  }
}

export const routes: Routes = [
  { path: 'auth-callback', component: AuthCallbackComponent },
  { path: 'auth-renew', component: AuthRenewComponent },
  {
    path: 'checkout/:orderNumber',
    component: CheckoutComponent,
    canActivate: [AuthGuardService, DemoGuardService],
  },
  {
    path: 'checkout/:orderNumber/transaction/:transactionId',
    component: CheckoutProcessingComponent,
    canActivate: [AuthGuardService, DemoGuardService],
  },
  {
    path: 'checkout/:orderNumber/complete',
    component: CheckoutResultComponent,
    canActivate: [AuthGuardService, DemoGuardService],
  },
  {
    path: 'checkout/:orderNumber/fail',
    component: CheckoutFailComponent,
    canActivate: [AuthGuardService, DemoGuardService],
  },
  {
    path: '',
    component: RedirectComponent,
    pathMatch: 'full',
  },
  {
    path: 'el',
    redirectTo: 'el/home',
    pathMatch: 'full',
  },
  {
    path: 'en',
    redirectTo: 'en/home',
    pathMatch: 'full',
  },
  {
    path: ':lang',
    canActivate: [CustomRedirectGuard],
    children: [
      { path: 'home', redirectTo: 'home', pathMatch: 'full' },
      {
        path: '',
        canActivate: [DemoGuardService, OnboardingGuardService],
        component: PortalShellComponent,
        children: [
          {
            path: 'home',
            loadChildren: () =>
              import('./features/home/home.module').then((x) => x.HomeModule),
          },
        ],
      },
      {
        path: 'account-settings',
        loadChildren: () =>
          import('./features/account-settings/account-settings.module').then(
            (x) => x.AccountSettingsModule,
          ),
        canActivate: [
          AuthGuardService,
          DemoGuardService,
          OnboardingGuardService,
        ],
      },
      {
        path: 'my-profile',
        canActivate: [
          AuthGuardService,
          DemoGuardService,
          OnboardingGuardService,
        ],
        loadChildren: () =>
          import('./features/my-profile/my-profile.routes').then(
            (mod) => mod.MY_PROFILE_ROUTES,
          ),
      },
      {
        path: 'properties/:propertyId',
        component: PropertyDetailsComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'properties',
        component: FindPropertyComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'welcome/:intent',
        component: OnboardingWelcomeComponent,
        canActivate: [DemoGuardService],
      },
      {
        path: 'terms-and-conditions',
        component: TermsAndConditionsComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'terms-and-conditions/:documentType',
        component: TermsAndConditionsComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'privacy-policy',
        component: PrivacyPolicyComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'listing/:propertyCode',
        component: PropertyListingComponent,
        canActivate: [
          AuthGuardService,
          DemoGuardService,
          PropertyOwnershipGuardService,
          OnboardingGuardService,
        ],
      },
      {
        path: 'listing/:propertyCode/complete',
        component: FinalStepComponent,
        canActivate: [
          AuthGuardService,
          DemoGuardService,
          PropertyOwnershipGuardService,
          OnboardingGuardService,
        ],
      },
      {
        path: 'sell-property-get-started',
        component: SellWithUsComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'property-upload-wizard',
        component: PropertyUploadWizardComponent,
        canActivate: [
          AuthGuardService,
          DemoGuardService,
          OnboardingGuardService,
        ],
      },
      {
        path: 'loan-application',
        component: LoanApplicationWizardComponent,
        canActivate: [
          AuthGuardService,
          DemoGuardService,
          OnboardingGuardService,
        ],
      },
      {
        path: 'loan-eligibility',
        component: LoanEligibilityWizardComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'services/:serviceType',
        component: ServiceDetailsComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'services/:serviceType/select-property',
        component: ServicePropertySelectorComponent,
        canActivate: [
          AuthGuardService,
          DemoGuardService,
          OnboardingGuardService,
        ],
      },
      {
        path: 'contact',
        component: ContactComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'sell-with-us',
        component: SellWithUsComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'cookies-settings',
        component: CookieSettingsComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'feedback/:requestId',
        component: FeedbackComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: 'landing',
        loadComponent: () =>
          import(
            './features/landing-page/landing-page/landing-page.component'
          ).then((c) => c.LandingPageComponent),
      },
      { path: 'register', component: RegistrationComponent },
      { path: 'login', component: LoginComponent },
      {
        path: 'on-boarding-buyer',
        canActivate: [DemoGuardService],
        component: OnboardingBuyerComponent,
      },
      {
        path: 'on-boarding-seller',
        canActivate: [DemoGuardService],
        component: OnboardingSellerComponent,
      },
      {
        path: 'faq',
        canActivate: [DemoGuardService, OnboardingGuardService],
        component: FaqComponent,
      },
      {
        path: 'about',
        canActivate: [DemoGuardService, OnboardingGuardService],
        component: AboutUsComponent,
      },
      {
        path: 'financing',
        canActivate: [DemoGuardService, OnboardingGuardService],
        component: FinancingComponent,
      },
      {
        path: 'services',
        canActivate: [DemoGuardService, OnboardingGuardService],
        component: ServicesComponent,
      },
      {
        path: 'testPayWizard',
        canActivate: [DemoGuardService, OnboardingGuardService],
        component: PaymentWizardComponent,
      },
      {
        path: 'error',
        canActivate: [DemoGuardService, OnboardingGuardService],
        component: ErrorComponent,
      },
      {
        path: 'not-found',
        component: NotFoundComponent,
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
      {
        path: '**',
        pathMatch: 'full',
        component: NotFoundComponent, // Directly load NotFoundComponent
        canActivate: [DemoGuardService, OnboardingGuardService],
      },
    ],
  },
  {
    path: 'error',
    canActivate: [DemoGuardService, OnboardingGuardService],
    component: ErrorComponent,
  },
  {
    path: 'not-found',
    component: NotFoundComponent,
    canActivate: [DemoGuardService, OnboardingGuardService],
  },
  { path: '**', pathMatch: 'full', redirectTo: 'not-found' },
];
